import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { BridgeComponent } from '../bridge/bridge.component';
import { SwapComponent } from '../swap/swap.component';
import { UniversalSeoResolved } from './seo-universal.resolver';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-seo-landing',
  standalone: true,
  imports: [CommonModule, BridgeComponent, SwapComponent],
  template: `
    <ng-container *ngIf="mode === 'bridge'; else showSwap">
      <app-bridge
        [bridgePrefill]="bridgePrefill"
        [seoPage]="seoPage">
      </app-bridge>
    </ng-container>

    <ng-template #showSwap>
      <app-swap
        [swapPrefill]="swapPrefill"
        [seoPage]="seoPage">
      </app-swap>
    </ng-template>
  `
})
export class SeoLandingComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private title = inject(Title);
  private meta = inject(Meta);
  private translate = inject(TranslateService);
  private doc = inject(DOCUMENT);

  private sub?: Subscription;

  mode: 'swap' | 'bridge' = 'swap';
  seoPage: any = null;

  swapPrefill?: { chainId: number; tokenIn: string; tokenOut: string };
  bridgePrefill?: { fromChainId: number; toChainId: number; tokenIn: string; tokenOut: string };

  ngOnInit(): void {
    this.sub = this.route.data.subscribe(data => {
      const resolved = data['seo'] as UniversalSeoResolved | null;
      if (!resolved) return;

      this.seoPage = resolved.resolved?.page ?? null;

      // Title + meta description
      const title = this.seoPage?.title ?? '';
      const desc = this.seoPage?.metaDescription ?? '';
      if (title) this.title.setTitle(title);
      if (desc) this.meta.updateTag({ name: 'description', content: desc });

      // Mode swap/bridge + prefill
      if (resolved.type === 'bridge') {
        this.mode = 'bridge';
        const c = (resolved.resolved as any).ctaParams;
        this.bridgePrefill = {
          fromChainId: c?.fromChainId,
          toChainId: c?.toChainId,
          tokenIn: c?.tokenIn,
          tokenOut: c?.tokenOut
        };
      } else {
        this.mode = 'swap';
        const c = (resolved.resolved as any).ctaParams;
        this.swapPrefill = {
          chainId: c?.chainId,
          tokenIn: c?.tokenIn,
          tokenOut: c?.tokenOut
        };
      }

      // === Canonical / og:url / hreflang (locales courtes) ===
      // On reconstruit l’URL canonique à partir des params de route :locale/:slug
      const locale = (this.route.snapshot.paramMap.get('locale') || '').toLowerCase();
      const slug = (this.route.snapshot.paramMap.get('slug') || '').replace(/^\/+|\/+$/g, '');
      const origin = (this.doc?.location?.origin) || 'https://swaptokens.app';

      const canonicalUrl =
        (locale && slug) ? `${origin}/${locale}/${slug}` :
          this.doc?.location?.href || `${origin}/`;

      this.setCanonical(canonicalUrl);
      this.setOgUrl(canonicalUrl);
      this.setSimpleHreflang(canonicalUrl, locale);
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  /** Insère/replace <link rel="canonical" href="..."> */
  private setCanonical(url: string): void {
    let link = this.doc.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /** Met à jour og:url pour cohérence SEO/social */
  private setOgUrl(url: string): void {
    this.meta.updateTag({ property: 'og:url', content: url });
  }

  /**
   * Ajoute un hreflang minimal compatible:
   *  - langue courante (si disponible)
   *  - x-default → canonique
   * (On évite d’inventer d’autres alternates si on n’a pas la liste.)
   */
  private setSimpleHreflang(url: string, lang: string): void {
    // Nettoyage des anciens xhtml:link (évite la duplication)
    const existing = this.doc.head.querySelectorAll("link[rel='alternate'][hreflang]");
    existing.forEach(el => el.parentElement?.removeChild(el));

    if (lang) {
      const l = this.doc.createElement('link');
      l.setAttribute('rel', 'alternate');
      l.setAttribute('hreflang', lang);
      l.setAttribute('href', url);
      this.doc.head.appendChild(l);
    }

    const xdef = this.doc.createElement('link');
    xdef.setAttribute('rel', 'alternate');
    xdef.setAttribute('hreflang', 'x-default');
    xdef.setAttribute('href', url);
    this.doc.head.appendChild(xdef);
  }
}
